import { useEffect, useState } from "react";
import { Pencil, Loader2, Save, HeartHandshake, Users, Bot, Lock, RefreshCw, Lightbulb } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { SiteHeader, SiteFooter } from "../components/landing/SiteChrome";

const SECTIONS = [
  { id: "why", icon: Lightbulb, title: "Why GetInterviewed was created", body: "Too many capable people struggle to communicate their experience well, to get through recruitment systems, or to understand why their applications aren't working. Sending the same CV to every job rarely helps. GetInterviewed exists to help people take a more informed, confident approach to finding work." },
  { id: "beliefs", icon: HeartHandshake, title: "What we believe", body: "Your real experience is enough to start with: it just needs to be presented clearly for each role. Honest applications beat inflated ones. Feedback should explain why, not just give a number. And the core tools should stay free, so cost is never the reason someone can't apply well." },
  { id: "who", icon: Users, title: "Who it's for", body: "Job seekers in Singapore and beyond: fresh graduates, early-career and mid-career professionals, PMETs, career switchers, people returning to work, mature workers and those stepping into management. It's built to be simple for a first-time job seeker and useful for experienced professionals." },
  { id: "ai", icon: Bot, title: "Responsible use of AI", body: "Our AI rewords and reorganises what's already in your CV. It must never invent qualifications, job titles, dates, achievements, metrics or skills. Anything a job needs that your CV doesn't show is flagged for you to confirm, or left out. You stay in control of every word you send." },
  { id: "privacy", icon: Lock, title: "Candidate privacy", body: "We don't share your information with employers. It only goes to a recruiter when you choose to send an application. Your CV text is processed by our AI providers to produce results, and we don't sell your data. See our Privacy Policy and PDPA Statement for full details." },
  { id: "improve", icon: RefreshCw, title: "Always improving", body: "GetInterviewed is a young product and we're improving it continually based on what helps people most. If something doesn't work for you, or you have an idea, we'd genuinely like to hear it." },
];

function FounderCard({ about, isAdmin, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(about);
  const [saving, setSaving] = useState(false);
  useEffect(() => setDraft(about), [about]);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/site/about", draft);
      onSaved(data);
      setEditing(false);
      toast.success("About page updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setSaving(false);
    }
  };
  const field = (k, label, rows) => (
    <label className="block">
      <span className="text-xs text-body">{label}</span>
      {rows ? (
        <textarea data-testid={`about-edit-${k}`} rows={rows} value={draft[k] || ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
          className="mt-1 w-full bg-canvas border border-line-strong rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:border-brand" />
      ) : (
        <input data-testid={`about-edit-${k}`} value={draft[k] || ""} onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
          className="mt-1 w-full bg-canvas border border-line-strong rounded-lg px-3 py-2 text-sm text-ink focus:outline-none focus:border-brand" />
      )}
    </label>
  );
  return (
    <section className="rounded-2xl border border-line bg-surface p-6 sm:p-8" data-testid="about-founder">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="font-mono2 text-xs uppercase tracking-widest text-brand mb-2">Who's behind it</div>
          <h2 className="font-display text-2xl font-bold" data-testid="about-founder-name">{about.founder_name || "Rukesh Varan"}</h2>
          <p className="text-body mt-1" data-testid="about-founder-role">{about.founder_role || "Founder · GetInterviewed is run by Digital Nomads Asia"}</p>
        </div>
        {isAdmin && !editing && (
          <button data-testid="about-edit-button" onClick={() => setEditing(true)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-warning/40 text-warning text-sm hover:bg-warning-soft">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-6 space-y-4">
          {field("founder_name", "Founder name")}
          {field("founder_role", "Role / one-line description")}
          {field("founder_story", "Founder story (shown publicly)", 6)}
          {field("contact_note", "Contact note (optional)", 2)}
          <div className="flex gap-2">
            <button data-testid="about-save-button" onClick={save} disabled={saving} className="flex items-center gap-2 px-5 py-2 rounded-xl bg-brand text-onbrand font-semibold hover:bg-brand-hover disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
            <button onClick={() => { setDraft(about); setEditing(false); }} className="px-4 py-2 rounded-xl text-body hover:text-ink">Cancel</button>
          </div>
        </div>
      ) : (
        <>
          {about.founder_story ? (
            <p className="mt-5 text-body leading-relaxed whitespace-pre-line" data-testid="about-founder-story">{about.founder_story}</p>
          ) : isAdmin ? (
            <p className="mt-5 text-sm text-warning/80 border border-dashed border-warning/40 rounded-lg p-4" data-testid="about-founder-placeholder">
              Admin only: add your founder story here. It stays hidden from visitors until you write it.
            </p>
          ) : null}
          {about.contact_note && <p className="mt-4 text-sm text-body" data-testid="about-contact-note">{about.contact_note}</p>}
        </>
      )}
    </section>
  );
}

export default function About() {
  const { user } = useAuth();
  const [about, setAbout] = useState({});
  useEffect(() => { api.get("/site/about").then(({ data }) => setAbout(data)).catch(() => {}); }, []);
  return (
    <div className="min-h-screen bg-canvas text-ink" data-testid="about-page">
      <SiteHeader />
      <main className="max-w-4xl mx-auto px-5 sm:px-6 py-16 sm:py-24">
        <div className="font-mono2 text-xs uppercase tracking-widest text-brand mb-3">About GetInterviewed</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-tight">Helping capable people show what they can do.</h1>
        <p className="mt-5 text-lg text-body leading-relaxed max-w-2xl">
          GetInterviewed is an AI career platform that helps job seekers understand their strengths, create stronger, honest applications and prepare with confidence.
        </p>
        <div className="mt-14 grid sm:grid-cols-2 gap-5">
          {SECTIONS.map((s) => (
            <section key={s.id} data-testid={`about-section-${s.id}`} className="rounded-xl border border-line bg-surface p-6">
              <s.icon className="w-5 h-5 text-brand mb-4" />
              <h2 className="font-display font-semibold text-lg mb-2">{s.title}</h2>
              <p className="text-sm text-body leading-relaxed">{s.body}</p>
            </section>
          ))}
        </div>
        <div className="mt-10">
          <FounderCard about={about} isAdmin={!!user?.is_admin} onSaved={setAbout} />
        </div>
        <p className="mt-10 text-sm text-faint">Questions about your data? Email <a href="mailto:privacy@getinterviewed.app" className="text-brand hover:underline">privacy@getinterviewed.app</a>.</p>
      </main>
      <SiteFooter />
    </div>
  );
}
